import { LocateFixed } from 'lucide-react'
import type { Map as MlMap } from 'maplibre-gl'

import type { Fix } from '../map/userLocationLayer'
import type { PermState } from '../location/useGeolocation'

import styles from './LocateMeFAB.module.css'

/**
 * LocateMeFAB — UI-SPEC §Components 5 (lines 293-315).
 *
 * State derivation:
 *   - `denied`  — perm === 'denied'
 *   - `loading` — fix is null and perm is not denied (first fix pending)
 *   - `active`  — map.getCenter ≈ fix (within ~50m)
 *   - `idle`    — otherwise
 *
 * Interaction:
 *   - idle / loading → `map.flyTo({ center, zoom: 15 })` (jumpTo under
 *     `prefers-reduced-motion: reduce`).
 *   - active → no-op (UI-SPEC line 310).
 *   - denied → calls `onOpenHelp` (opens the PermissionBanner help link).
 */

export interface LocateMeFABProps {
  map: MlMap | null
  fix: Fix | null
  perm: PermState
  onOpenHelp?: () => void
}

type FabState = 'idle' | 'active' | 'loading' | 'denied'

const ACTIVE_TOLERANCE_M = 50

function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6_371_000
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const aa =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(aa))
}

function deriveState(
  map: MlMap | null,
  fix: Fix | null,
  perm: PermState,
): FabState {
  if (perm === 'denied') return 'denied'
  if (!fix) return 'loading'
  if (!map) return 'idle'
  const center = map.getCenter()
  const dist = haversineMeters(
    { lat: fix.lat, lng: fix.lng },
    { lat: center.lat, lng: center.lng },
  )
  return dist < ACTIVE_TOLERANCE_M ? 'active' : 'idle'
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export function LocateMeFAB({ map, fix, perm, onOpenHelp }: LocateMeFABProps) {
  const state = deriveState(map, fix, perm)

  const labels: Record<FabState, string> = {
    idle: 'Center map on my location',
    active: 'Recentre on my location',
    loading: 'Center map on my location',
    denied: 'Location access denied',
  }

  const classes = [styles.fab, styles[state] ?? ''].filter(Boolean).join(' ')

  const handleClick = () => {
    if (state === 'denied') {
      onOpenHelp?.()
      return
    }
    if (state === 'active') return
    if (!map || !fix) return
    if (prefersReducedMotion()) {
      map.jumpTo({ center: [fix.lng, fix.lat], zoom: 15 })
    } else {
      map.flyTo({ center: [fix.lng, fix.lat], zoom: 15 })
    }
  }

  return (
    <button
      type="button"
      className={classes}
      aria-label={labels[state]}
      aria-pressed={state === 'active'}
      aria-disabled={state === 'denied'}
      onClick={handleClick}
    >
      <LocateFixed
        size={24}
        strokeWidth={2}
        className={styles.icon}
        aria-hidden="true"
      />
    </button>
  )
}
